"use client";

import { useEffect, useRef, useSyncExternalStore } from "react";
import Script from "next/script";
import { usePathname } from "next/navigation";
import { useAuthStore } from "@/lib/store/auth-store";
import { useLocale } from "@/lib/i18n/client";
import { deleteAnalyticsCookies, useConsentStore } from "@/lib/tracking/consent";
import { startSiteVisitTracking } from "@/lib/tracking/site-visit";
import { recordSectionView } from "@/lib/tracking/sections";
import { analyticsEnabled, GA_ID, internalMetricsEnabled, initGtag, markInternal, resolveConsent, setUserProperties, TRACK_EVENT_ATTR, TRACK_PARAMS_ATTR } from "@/lib/tracking/track";

const CLARITY_ID = "x3ls3ozg0m";

const CLARITY_INIT = `(function(c,l,a,r,i,t,y){
  c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
  t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
  y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
})(window,document,"clarity","script","${CLARITY_ID}");
window.clarity('consentv2',{ad_Storage:'denied',analytics_Storage:'granted'});`;

const noopSubscribe = () => () => {};

// GA4 se carga siempre en producción (visitas); los eventos de acciones y Clarity, solo con
// consentimiento. También fija las propiedades de usuario y manda los eventos
// declarados con trackAttrs() en el HTML.
export function AnalyticsManager() {
  const choice = useConsentStore((s) => s.choice);
  const load = useConsentStore((s) => s.load);
  const user = useAuthStore((s) => s.user);
  const authChecked = useAuthStore((s) => s.checked);
  const lang = useLocale();
  const previousChoice = useRef(choice);
  // Se decide en el navegador (dominio y marca interna); el servidor no carga nada.
  const isSuperadmin = user?.role === "superadmin";
  const enabled = useSyncExternalStore(noopSubscribe, analyticsEnabled, () => false) && !isSuperadmin;
  const metricsEnabled = useSyncExternalStore(noopSubscribe, internalMetricsEnabled, () => false) && !isSuperadmin;
  const pathname = usePathname();

  useEffect(() => {
    initGtag();
    load();
  }, [load]);

  // Un superadmin deja de medirse desde ya y en adelante en este navegador,
  // aunque después navegue sin sesión.
  useEffect(() => {
    if (isSuperadmin) markInternal(true);
  }, [isSuperadmin]);

  const loaded = useConsentStore((s) => s.loaded);

  // Va antes que los demás efectos: envía o descarta los eventos pendientes
  // apenas se conoce la elección.
  useEffect(() => {
    if (loaded) resolveConsent(choice === "granted");
  }, [loaded, choice]);

  // Retirar el consentimiento: se borran las cookies de Clarity y se recarga
  // para descargar su script (no hay forma limpia de "apagarlo" en caliente).
  useEffect(() => {
    if (previousChoice.current === "granted" && choice === "denied") {
      deleteAnalyticsCookies();
      window.location.reload();
    }
    previousChoice.current = choice;
  }, [choice]);

  // analytics_consent permite separar en GA4 a quienes tienen recorrido
  // completo (granted) de quienes solo aportan visitas.
  useEffect(() => {
    if (!loaded || !authChecked) return;
    setUserProperties({
      language: lang,
      logged_in: user ? "yes" : "no",
      user_role: user?.role ?? "anonymous",
      analytics_consent: choice ?? "pending",
    });
  }, [loaded, choice, authChecked, user, lang]);

  useEffect(() => {
    if (choice !== "granted") return;
    function onClick(e: MouseEvent) {
      const el = (e.target as Element | null)?.closest?.(`[${TRACK_EVENT_ATTR}]`);
      if (!el) return;
      const name = el.getAttribute(TRACK_EVENT_ATTR);
      if (!name) return;
      try {
        const params = JSON.parse(el.getAttribute(TRACK_PARAMS_ATTR) ?? "{}");
        window.gtag?.("event", name, params);
      } catch {}
    }
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [choice]);

  // Métricas internas (visitas, secciones): siempre, sin depender del aviso;
  // sin navegadores internos, automatizados ni superadmin (se espera a saber
  // quién es para no contar su visita). Ver internalMetricsEnabled.
  useEffect(() => {
    if (!metricsEnabled || !authChecked) return;
    return startSiteVisitTracking();
  }, [metricsEnabled, authChecked]);

  useEffect(() => {
    if (!metricsEnabled || !authChecked) return;
    recordSectionView(pathname);
  }, [metricsEnabled, authChecked, pathname]);

  if (!enabled) return null;

  return (
    <>
      <Script src={`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`} strategy="afterInteractive" />
      {choice === "granted" && (
        <Script id="clarity-init" strategy="afterInteractive" dangerouslySetInnerHTML={{ __html: CLARITY_INIT }} />
      )}
    </>
  );
}
